import { Trans, useTranslation } from 'react-i18next';
import FullBlockingModal from '../common/FullBlockingModal';
import { useState } from 'react';
import { Button } from '../common/Button';
import { ChildQuote, Quote } from '../../types/quote';
import { useQuoteStore } from '../../stores/quoteStore';
import { JobQuote } from '../../types/job';

interface RequestChangeQuoteModalProps {
  /** The quote we are viewing and requesting a change for (principal or child). */
  freelancerQuote: Quote | ChildQuote | JobQuote;
  parentQuote: Quote | ChildQuote | JobQuote | null;
  onClose: () => void;
}

export function RequestChangeQuoteModal({
  onClose,
  freelancerQuote,
}: RequestChangeQuoteModalProps) {
  const { t } = useTranslation(['freelancers', 'common', 'quotes']);
  const [sending, setSending] = useState(false);
  const [quoteRequestChangeMessage, setQuoteRequesChangetMessage] = useState('');
  const [quoteRequesChangetError, setQuoteRequesChangetError] = useState<string | null>(null);
  const [isRequestConfirm, setIsRequestConfirm] = useState(false);

  const { requestQuoteChange } = useQuoteStore();

  const isMessageValid = quoteRequestChangeMessage.trim().length >= 3;

  const handleSubmit = async () => {
    if (!freelancerQuote?.id || !isMessageValid) return;

    setSending(true);
    setQuoteRequesChangetError(null);
    try {
      await requestQuoteChange(freelancerQuote.id, quoteRequestChangeMessage);
      onClose();
      setIsRequestConfirm(true);
    } catch (error) {
      console.error(error);
      setQuoteRequesChangetError(t('requestChangeError', { ns: 'quotes' }));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[72rem]">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
          onClick={onClose}
          aria-label={t('common:form_actions.close')}
        >
          &times;
        </button>

        <>
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold text-secondary pt-6 md:pt-0 mb-4">
              {t('request_changes', { ns: 'quotes', defaultValue: "Demande d'ajustement" })}
            </h2>

            <p className="mb-4">
              <Trans
                i18nKey={'quote_change_message_hint'}
                ns={'quotes'}
                values={{ name: freelancerQuote?.quote_owner.first_name }}
                components={{ bold: <span className="font-bold" /> }}
              />
            </p>

            <div className="flex flex-col gap-2 mb-8">
              <label className="text-secondary" htmlFor="requestMessage">
                <Trans
                  i18nKey={'quote_change_message_for_label'}
                  ns={'quotes'}
                  values={{ name: freelancerQuote?.quote_owner.first_name }}
                  components={{ bold: <span className="font-bold" /> }}
                />
              </label>
              <textarea
                name="requestMessage"
                value={quoteRequestChangeMessage}
                onChange={e => setQuoteRequesChangetMessage(e.target.value)}
                placeholder={t('requestChangeMessagePlaceholder', { ns: 'quotes' })}
                className="w-full h-[8.3125rem] border-0 px-3 py-2 mt-2 font-ambit text-[0.8125rem] not-italic font-normal leading-[1.25rem] bg-[#E1E9F0] md:h-[5.3125rem]"
                maxLength={1000}
              />
              {quoteRequesChangetError && (
                <div className="text-[#F04438] text-[0.9375rem] font-ambit mt-1">
                  {quoteRequesChangetError}
                </div>
              )}
            </div>

            <hr className="hidden border-[#E1E9F0] mb-4 md:block" />

            <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
              <Button
                className="w-full md:w-auto md:px-8"
                variant="primary"
                onClick={handleSubmit}
                disabled={!freelancerQuote?.id || !isMessageValid}
              >
                {sending
                  ? t('common:form_actions.sending', { ns: 'common', defaultValue: 'Envoie...' })
                  : t('common:form_actions.send', { ns: 'common', defaultValue: 'Envoie' })}
              </Button>
            </div>
          </div>
        </>
      </FullBlockingModal>

      {isRequestConfirm && (
        <FullBlockingModal innerClassName="md:max-w-xl">
          <h2 className="text-xl font-bold mb-4 text-secondary">
            {t('quote_change_request_sent', { ns: 'quotes' })}
          </h2>
          <p className="mb-4 text-secondary">
            <Trans
              i18nKey="quote_change_notification"
              ns="quotes"
              components={{
                bold: <span className="font-bold" />,
              }}
              values={{
                name: freelancerQuote?.quote_owner.first_name,
              }}
            />
          </p>
          <div className="flex gap-4 justify-end mt-4">
            <Button
              variant="primary"
              className="w-full md:w-auto md:px-8"
              onClick={() => {
                setIsRequestConfirm(false);
              }}
            >
              {t('createQuoteModal.backToSubmissionsButtonLabel')}
            </Button>
          </div>
        </FullBlockingModal>
      )}
    </>
  );
}
